// Image formatting utilities

/**
 * Format avatar URL with fallback and resizing
 * @param avatarUrl Raw avatar URL
 * @param size Desired size in pixels
 * @returns Formatted avatar URL
 */
export function formatAvatarUrl(avatarUrl: string | null, size = 150): string {
	if (!avatarUrl) {
		return `https://api.dicebear.com/7.x/initials/svg?size=${size}&backgroundType=gradientLinear`;
	}

	// Special case: If the URL contains "data:image" but is not at the start,
	// it's likely a malformed URL with a data URL embedded inside it
	if (avatarUrl.includes("data:image") && !avatarUrl.startsWith("data:")) {
		// Extract the data URL part
		const dataMatch = avatarUrl.match(
			/(data:image\/(jpeg|png|gif|webp);base64,[^"']+)/,
		);
		if (dataMatch?.[1]) {
			console.log("Extracted embedded data URL from malformed avatar URL");
			return dataMatch[1];
		}
	}

	// If it's a data URL, return it directly
	if (avatarUrl.startsWith("data:")) {
		return avatarUrl;
	}

	// If already a full URL with http/https, return as is
	if (avatarUrl.startsWith("http")) {
		return avatarUrl;
	}

	// If it's a storage path, return it as-is (the app layer will handle full URL construction)
	return avatarUrl;
}

/**
 * Get user initial for avatar fallback (prioritizes username)
 * @param username User's username (preferred)
 * @param displayName User's display name (fallback)
 * @returns Single character initial
 */
export function getUserInitials(
	username?: string | null,
	displayName?: string | null,
): string {
	// Prioritize username if available
	const name = username || displayName;

	if (!name) return "??";

	// Get first character of the name
	const trimmed = name.trim();
	if (trimmed.length === 0) return "??";

	return trimmed.charAt(0).toUpperCase();
}
